import { BillingModel, PolicyType } from '../types/database/billing-svc';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

export function findRevenueShare(plans, date: Date) {
  return plans.find(plan => plan.voidedAt === null || dayjs(date).utc() < dayjs(plan.voidedAt).utc());
}
export function findPlanForDate(plans, date: Date) {
  return plans.length === 1 ? plans[0] : findRevenueShare(plans, date);
}

/** Plan shape with rev share percentages (avoids coupling to Prisma). */
export type PlanWithRevShare = {
  shippingRevSharePercentage: number;
  extendedWarrantyRevSharePercentage: number | null;
};

/**
 * Returns the rev share percentage to use for a bill given its policy type and the applicable plan.
 * EXTENDED_WARRANTY uses extendedWarrantyRevSharePercentage, falling back to shipping; otherwise uses shipping.
 */
export function getRevSharePercentageForPolicy(
  policyType: PolicyType | string,
  plan: PlanWithRevShare | null | undefined,
): number {
  if (!plan) return 0;
  if (policyType === PolicyType.EXTENDED_WARRANTY) {
    return plan.extendedWarrantyRevSharePercentage ?? plan.shippingRevSharePercentage;
  }
  return plan.shippingRevSharePercentage;
}

export const isNet = (billingModel: BillingModel | string | null | undefined): boolean =>
  billingModel === BillingModel.NET;

export const netIgnoresDiscounts = (
  billingModel: BillingModel | string | null | undefined,
  deductNetDiscounts: boolean | null | undefined,
): boolean => isNet(billingModel) && !deductNetDiscounts;

export const netDeductsDiscounts = (
  billingModel: BillingModel | string | null | undefined,
  deductNetDiscounts: boolean | null | undefined,
): boolean => isNet(billingModel) && !!deductNetDiscounts;

/**
 * Computes the rev-share dollars for a single bill/policy under the applicable plan.
 *
 * Picks the right plan from the `plans` history by date (or the only plan if just one),
 * uses `amount` for NET cycles and `customerAmount` otherwise, and applies the policy-type-aware
 * rev-share percentage. Returns both the base amount used and the unrounded rev-share amount —
 * callers that need whole-dollar values should apply `Math.round` themselves.
 */
export function calculateRevShareAmount(
  bill: { date: Date; amount: number; customerAmount: number; policyType: PolicyType | string },
  plans: (PlanWithRevShare & { voidedAt?: Date | null })[],
  billingModel?: BillingModel | string | null,
): { baseAmount: number; revShareAmount: number } {
  const plan = findPlanForDate(plans, bill.date);
  const baseAmount = (isNet(billingModel) ? bill.amount : bill.customerAmount) ?? 0;
  const revSharePct = getRevSharePercentageForPolicy(bill.policyType, plan) ?? 0;
  return { baseAmount, revShareAmount: (baseAmount * revSharePct) / 100 };
}

export function correctCurrentId(id: string): string | null | undefined {
  return id === 'current' ? null : id || undefined;
}

export function calculateRevenueBeforePayout(invoice: { baseAmount: number; usageAmount: number; correctionAmount: number }) {
  return invoice.baseAmount + invoice.usageAmount + invoice.correctionAmount;
}

export function getBillAmount({
  bill,
  startDate,
  endDate,
  billingModel,
  deductNetDiscounts,
}: {
  bill;
  startDate: Date;
  endDate: Date;
  billingModel?: BillingModel | string | null;
  deductNetDiscounts?: boolean | null;
}) {
  const canceledWithinCycle = dayjs(bill.voidedAt) > dayjs(startDate) && dayjs(bill.voidedAt) < dayjs(endDate);

  const discount = netIgnoresDiscounts(billingModel, deductNetDiscounts) ? 0 : bill.discount;
  const net = bill.amount - discount;

  // Plain decimals, no currency symbol or thousands separators — these land in CSV exports that
  // merchants reconcile in a spreadsheet.
  if (!bill.voidedAt) {
    return (net / 100).toFixed(2);
  }
  if (bill.voidedAt && dayjs(bill.date) > dayjs(startDate) && canceledWithinCycle) {
    return '0.00';
  }
  if (dayjs(bill.voidedAt) > dayjs(endDate)) return (net / 100).toFixed(2);

  return (-net / 100).toFixed(2);
}
